import {
  generalTerm,
} from './pl.shared.ts';

import {
  TComponentPT9800Keys, TComponentPT9800Value, TComputedPT9800PlannedPartInput,
  Tpt9800TrlTokens,
} from '@/types/openapi-type.ts';

const processFlowManualCommand: TComponentPT9800Keys<'processFlowManualCommand'> = {
  acknowledged: 'Potwierdzono',
  additionalInfoColumnCaption: 'Dodatkowy podpis informacyjny',
  additionalInfoColumnValue: 'Dodatkowa wartość informacji',
  idFieldName: 'Nazwa pola ID',
  idFieldValue: 'Wartość pola ID',
  ldArchivCrit1Name: 'Podpis Archiv-Crit1',
  ldArchivCrit1Value: 'Wartość Archiv-Crit1',
  orderNumberCaption: 'Podpis numeru zamówienia',
  orderNumberValue: 'Wartość numeru zamówienia',
  priority: 'Priorytet',
  rowId: 'Identyfikator wiersza',
  source: 'Źródło',
  sourceWpId: 'Identyfikator źródłowego WP',
  target: 'Cel',
  targetWpId: 'Identyfikator docelowego WP',
  wpId: 'Identyfikator WP samochodu',
};

const recipeRuntimeInfo: TComponentPT9800Keys<'recipeRuntimeInfo'> = {
  actualStepDuration: 'Rzeczywisty czas trwania kroku',
  loadIdentifier: generalTerm.loadIdentifier,
};

const recipeListItem: TComponentPT9800Keys<'recipeListItem'> = {
  id: 'id',
  description: generalTerm.description,
  recipe: generalTerm.recipe,
  recipePrefix: 'Prefiks receptury',
  version: 'Wersja receptury',
};

const recipeItemHeader: Partial<TComponentPT9800Keys<'batchRecipeItemHeader'>> = {
  recipeName: 'Nazwa',
  version: generalTerm.revision,
  recipePrefix: 'Grupa',
  description: 'Opis',
  processType: 'Proces',
  processId: 'Działanie',
  comment: 'Komentarz',
  diff: 'Aktywuj',
  correction: 'Korekta',
};

const material: Partial<TComponentPT9800Keys<'material'>> = {
  // Diffusion field labels reuse the shared generalTerm wording so they read identically here, in
  // the Diffusion Inputs panel and in the simulator.
  carbonContent: generalTerm.carbonContent,
  carbideLimit: generalTerm.carbideLimit,
  alloyFactor: generalTerm.alloyFactor,
  customerMaterialId: 'Materiał',
  endTemperature: generalTerm.endTemperature,
  grainSize: generalTerm.grainSize,
  quenchingIntensity: generalTerm.quenchingIntensity,
  representativeDiameter: generalTerm.representativeDiameter,
  requiredHardness: generalTerm.requiredHardness,
  targetSurfC: generalTerm.targetSurfaceCarbon,
  co: generalTerm.carbonMonoxide,
  h2: generalTerm.hydrogen,
};

const beltRecipeItemHeader: Partial<TComponentPT9800Keys<'beltRecipeItemHeader'>> = {
  description1: 'Opis 1',
  description2: 'Opis 2',
  description3: 'Opis 3',
  description4: 'Opis 4',
  processId: 'Działanie',
  processDepth: 'Głębokość nawęglania',
  processDepthAt: 'Zawartość węgla na głębokości nawęglania',
};

const pusherRecipeItemHeader: Partial<TComponentPT9800Keys<'pusherRecipeItemHeader'>> = {
  description: beltRecipeItemHeader.description1,
  description1: beltRecipeItemHeader.description2,
  processDepth: beltRecipeItemHeader.processDepth,
  processDepthAt: beltRecipeItemHeader.processDepthAt,
  caseHardeningDepthFrom: 'Głębokość warstwy utwardzonej od',
  caseHardeningDepthTo: 'Głębokość warstwy utwardzonej do',
  endTemperature: generalTerm.endTemperature,
  grainSize: generalTerm.grainSize,
  quenchIntensity: generalTerm.quenchingIntensity,
  partId: generalTerm.part,
};

const pusherRecipeRuntimeInfo: Partial<TComponentPT9800Keys<'pusherRecipeRuntimeInfo'>> = {
  timeStamp: generalTerm.date,
  description: 'Opis miejsca',
  instanceNoteText1: 'Uwaga 1',
  instanceNoteText2: 'Uwaga 2',
  instanceNoteText3: 'Uwaga 3',
};

const recipeItemPosition: Partial<TComponentPT9800Keys<'recipeItemPosition'>> = {
  setpoints: 'Punkty nastawcze',
  endAfter: 'Warunek końcowy',
  digitalOutputList: 'Wyjścia cyfrowe',
  digitalInputList: 'Wejścia cyfrowe',
};

const recipeItemPositionHeader: Partial<TComponentPT9800Keys<'recipeItemPositionHeader'>> = {
  positionNo: 'Krok',
  description: 'Nazwa kroku',
  chamberId: 'Izba',
  chamberOperationId: 'Operacja komorowa',
  afterId: 'Warunek końcowy',
  estimatedDurationInMin: 'Szacowany czas trwania',
  endAtPerc: 'Koniec na %cd',
  afterTimeInMin: 'Czas trwania warunku końcowego',
};

const plannedPartInput: Partial<Record<keyof TComputedPT9800PlannedPartInput, string>> = {
  pos: 'Pozycja',
  partName: 'Numer części',
  partDescription: 'Opis części',
  orderNo: generalTerm.loadIdentifier,
  orderRemark: 'Uwaga do zamówienia',
  customerOrderNumber: 'Numer zamówienia klienta',
  amount: 'Kwota',
  weight: 'Waga',
  totalWeight: 'Masa całkowita',
};

const processFlowContent: Partial<TComponentPT9800Keys<'processFlowContent'>> = {
  name: 'Nr zawartości',
  weight: 'Waga PLC',
  createdTimestamp: 'Utworzono',
  modifiedTimestamp: 'Zmodyfikowano',
};

const processFlow: Partial<TComponentPT9800Keys<'processFlow'>> = {
  name: generalTerm.name,
  description: generalTerm.description,
  updateTimestamp: 'Ostatnia aktualizacja',
  automatNo: 'Automat Nie',
  comment1: 'Komentarz1',
  comment2: 'Komentarz 2',
  comment3: 'Komentarz 3',
};

const processFlowListItem: Partial<TComponentPT9800Keys<'processFlowListItem'>> = {
  name: generalTerm.name,
  description: generalTerm.description,
};

const planLoadInput: Partial<TComponentPT9800Keys<'planLoadInput'>> = {
  workplaceWoNumber: generalTerm.loadIdentifier,
};

const setPointItem: Partial<TComponentPT9800Keys<'setPointItem'>> & {
  unit: string,
} = {
  userComponentId: 'Punkt nastawy',
  controlId: 'Klawisz kontrolny',
  value: 'Wartość',
  unit: 'Jednostka',
  toleranceMax: 'Tol +',
  toleranceMin: 'Tol -',
  alarmStartTimeInMin: 'Start alarmu',
  alarmToleranceTimeInMin: 'Tolerancja alarmu',
  endControlId: 'Koniec ctrl',
  endValue: 'Warunek końcowy',
};

const furnaceAlarm: Partial<TComponentPT9800Keys<'furnaceAlarm'>> = {
  wpName: 'Miejsce pracy',
  ackTime: 'Ack',
  alarmBit: 'Fragment',
  alarmByte: 'Bajt',
  endTime: 'Koniec',
  gate: 'Brama',
  alarmNo: 'Id',
  message: 'Wiadomość',
  priority: 'Priorytet',
  startTime: 'Start',
};

const load: Partial<TComponentPT9800Keys<'load'>> = {
  name: generalTerm.jobDisplayName,
  startTime: generalTerm.jobStartedAtUtc,
  endTime: generalTerm.jobFinishedAtUtc,
  identifier: generalTerm.loadIdentifier,
  recipe: generalTerm.recipe,
  partsText: plannedPartInput.partDescription,
  workOrderNo: plannedPartInput.orderNo,
  orderRemark: plannedPartInput.orderRemark,
  status: 'Status',
  comment: 'Komentarz',
  processFlowLoadName: 'Wsad procesu',
  packedTime: 'Czas pakowania',
  packedUser: 'Spakowane przez',
  startedUser: 'Uruchomione przez',
  earliestTimeStart: 'Najwcześniejszy start',
};

const loadState: Record<TComponentPT9800Value<'load'>['status'], string> = {
  EXT_START: 'Rozpoczęto (zewnętrznie)',
  FINISHED: 'Gotowy',
  PLANNED: 'Planowany',
  RUNNING: 'Działanie',
  RUNNING_LOADING_ENDED: 'Uruchomiony (ładowanie zakończone)',
  STARTED: 'Rozpoczęto',
  STOPPED: 'Zatrzymany',
  TOO_OLD_NEVER_STARTED: 'Nigdy nie uruchomiono',
  UNKNOWN: 'Nieznany',
  WAITING_FOR_CONDITIONS: 'Oczekiwanie na warunki',
  WAITING_FOR_LOADING: 'Oczekiwanie na załadunek',
};

const WorkPlaceStatus: Partial<TComponentPT9800Keys<'WorkPlaceStatus'>> = {
  connected: 'połączony',
  recipe: 'Aktualny krok',
  remote: 'zdalny',
  hold: 'Wstrzymaj',
};

const WorkPlaceStatusStatus: Record<Exclude<TComponentPT9800Value<'WorkPlaceStatus'>['status'], undefined>, string> = {
  ALARM: 'Alarm',
  IDLE: 'Bezczynny',
  NO_REMOTE_CONNECTION: 'Brak połączenia zdalnego',
  RUNNING: 'Receptura w toku',
  SWITCHED_OFF: 'Wyłączony',
};

export const pt9800Translations = {
  T_MENU_GROUP_USER: 'Użytkownik',
  T_MENU_GROUP_TERMINAL: 'Terminal',
  T_MENU_GROUP_SYSTEM_1: 'System-1',
  T_MENU_GROUP_SYSTEM_2: 'System-2',
  T_MENU_ITEM_USER_GROUPS: 'Grupy użytkowników',
  T_MENU_ITEM_USERS: 'Użytkownicy',
  T_MENU_ITEM_APPLICATION_GROUPS: 'Grupy aplikacji',
  T_MENU_ITEM_APPLICATIONS: 'Aplikacje',
  T_MENU_ITEM_USER_ACCESS: 'Dostęp użytkowników',
  T_MENU_ITEM_MATERIAL_MANAGEMENT: 'Materiały',
  T_MENU_ITEM_PART_MANAGEMENT: 'Zarządzanie częściami',
  T_MENU_ITEM_PART_CUSTOMERS: 'Klienci',
  T_MENU_ITEM_TERMINAL_TYPE: 'Typ terminala',
  T_MENU_ITEM_TERMINAL: 'Terminal',
  T_MENU_ITEM_CLIENT_ACCESS: 'Dostęp klienta',
  T_MENU_ITEM_COLOR_ADMINISTRATION: 'Kolory',
  T_MENU_ITEM_BOX_ADMINISTRATION: 'Pudełka',
  T_MENU_ITEM_ALARMS: 'Alarmy',
  T_MENU_ITEM_BACKGROUND_PROCESSES: 'Procesy w tle',
  T_MENU_ITEM_TASKS: 'Zadania',
  T_MENU_ITEM_WORKPLACES: 'Stanowiska pracy',
  T_MENU_ITEM_OPERATIONS: 'Operacje',
  T_MENU_GROUP_SYSTEM_DATA: 'Dane systemowe',
  T_MENU_GROUP_EVALUATION: 'Ocena',
  T_MENU_GROUP_PART: 'Część',
  T_MENU_GROUP_SPECIAL: 'Specjalne',
  T_MENU_ITEM_PRODUCTION_LOGS: 'Dzienniki produkcji',
  T_MENU_ITEM_WORKPLACE_TYPE: 'Typ stanowiska pracy',

  T_SHOP_FURNACE_STATUS_NOT_CONNECTED: 'Niebieski=Urządzenie niepodłączone',
  T_SHOP_FURNACE_STATUS_MANUAL_MODE: 'Biały=Tryb ręczny',
  T_SHOP_FURNACE_STATUS_NO_TREATMENT_RUNNING: 'Żółty=Brak aktywnej obróbki cieplnej',
  T_SHOP_FURNACE_STATUS_PLC_DENIES_START: 'Różowy=PLC odmawia uruchomienia recepty',
  T_SHOP_FURNACE_STATUS_RUNNING: 'Zielony=Obróbka cieplna w toku - brak alarmów',
  T_SHOP_FURNACE_STATUS_ALARM: 'Czerwony=Występują alarmy',
  T_SHOP_FURNACE_STATUS_OFF: 'Jasnoszary=Wyłączone (patrz dane bazowe stanowiska)',
  T_SHOP_FURNACE_STATUS_MAINTENANCE: 'Ciemnoszary=W trybie konserwacji (patrz dane bazowe stanowiska)',
  T_SHOP_FURNACE_STATUS_CONFIG_ERROR: 'Cyjan=Błąd konfiguracji',
  T_SHOP_FURNACE_STATUS_ENERGY_MAN_DISABLED_START: 'Różowy=Zarządzanie energią blokuje start recepty',
  T_SHOP_FURNACE_STATUS_WEEKEND_MODE: 'Tryb weekendowy',
  T_SHOP_FURNACE_STATUS_NO_ALARMS: 'Zielony=Brak alarmów',

  // SHOPLAYOUT - CAR
  T_SHOP_STATUS_CELL_READY_FOR_WORK: 'Komórka gotowa do pracy',
  T_SHOP_STATUS_CELL_NOT_READY_FOR_WORK: 'Komórka NIE jest gotowa do pracy !!!',

  T_SHOP_CAR_STATUS_NO_PLC_SIMULATE: '!!! BRAK PLC ⇒ Symulacja !!!',
  T_SHOP_CAR_STATUS_MANUAL: 'RĘCZNY',
  T_SHOP_CAR_STATUS_AUTO: 'Auto',
  T_SHOP_CAR_STATUS_SCHED: 'SCHED',
  T_SHOP_CAR_STATUS_NOT_ACTIVE: 'WÓZEK ZAŁADUNKOWY NIEAKTYWNY',
  T_SHOP_CAR_STATUS_LOGIC_NOT_RUNNING: 'Logika nie działa',
  T_SHOP_CAR_STATUS_NOT_IN_ZERO_POS: 'Nie w pozycji zerowej',
  T_SHOP_CAR_STATUS_S_ACTIVE: 'SK aktywny',
  T_SHOP_CAR_STATUS_CRANE: 'Suwnica',
  T_SHOP_CAR_STATUS_S: 'S',
  T_SHOP_CAR_STATUS_T: 'T',
  T_SHOP_CAR_STATUS_CREQ: 'CReq',
  T_SHOP_CAR_STATUS_REQ: 'Req',
  T_SHOP_CAR_STATUS_WANT_CANCEL: 'Żądanie anulowania',
  T_SHOP_CAR_STATUS_CANCEL: 'Anuluj',
  T_SHOP_CAR_STATUS_NOT_VALID: 'Nieprawidłowy',
  T_SHOP_CAR_STATUS_REF: 'Ref.',
  T_SHOP_CAR_STATUS_RUNNING: 'W trakcie',
  T_SHOP_CAR_STATUS_FINISHED: 'Zakończony',
  T_SHOP_CAR_STATUS_WILL_END: 'Zostanie zakończony',
  T_SHOP_CAR_STATUS_TOS: 'do S',
  T_SHOP_CAR_STATUS_LDS: 'ładuj S',
  T_SHOP_CAR_STATUS_SLD: 'S załadowane',
  T_SHOP_CAR_STATUS_TOD: 'do D',
  T_SHOP_CAR_STATUS_DL: 'rozładuj',
  T_SHOP_CAR_STATUS_DLD: 'D rozładowane',

  T_SHOP_CAR_CONTROL_CRANE: 'Suwnica w trybie SCHED',
  T_SHOP_CAR_1_SCHED_MODE: 'Wózek 1 - tryb SCHED',
  T_SHOP_CAR_2_SCHED_MODE: 'Wózek 2 - tryb SCHED',
  T_SHOP_CAR_3_SCHED_MODE: 'Wózek 3 - tryb SCHED',
  T_SHOP_CAR_4_SCHED_MODE: 'Wózek 4 - tryb SCHED',
  T_SHOP_CAR_OIL_QUENCH_SCHEDULED: 'Hartowanie w oleju zaplanowane',
  T_SHOP_CAR_IN_SCHEDULED_MODE: 'Wózek w trybie planowanym',
  T_SHOP_CAR_SCHEDULED: 'Zaplanowane',
  T_SHOP_STORAGE_SCHEDULED: 'Magazyn w trybie SCHED',

  // Stanowisko – tekst pod boxem
  T_SHOP_PLACE_STATUS_FINISHED: 'Zakończone',
  T_SHOP_PLACE_STATUS_CHECK: 'Sprawdź',
  T_SHOP_PLACE_STATUS_ASAP: 'Pilne',
  T_SHOP_PLACE_STATUS_PCHECK: 'Kontrola prod.',
  T_SHOP_PLACE_STATUS_WCHECK: 'Kontrola towaru',
  T_SHOP_PLACE_STATUS_WAIT: 'Czekaj',
  T_SHOP_PLACE_STATUS_MANUAL: 'Ręczne',

  // SHOPLAYOUT TOOLTIPS
  T_SHOP_TT_LOCK_COMMENT: 'Komentarz blokady',
  T_SHOP_TT_LOAD_NO: 'Nr wsadu',
  T_SHOP_TT_IS_ON_PLACE: 'znajduje się na miejscu',
  T_SHOP_TT_PLACE: 'Miejsce',
  T_SHOP_TT_BACK_TO_PLACE: 'Powrót do stanowiska nr',
  T_SHOP_TT_PRIORITY: 'Priorytet',
  T_SHOP_TT_IS_RESERVED_FROM: 'zarezerwowane od',
  T_SHOP_TT_TEST_LOAD: 'Wsad testowy',
  T_SHOP_TT_LONG_LOAD: 'Długi wsad',
  T_SHOP_TT_PF_NO: 'Nr procesu',
  T_SHOP_TT_RECIPE: 'Recepta',
  T_SHOP_TT_PLANNED: 'Zaplanowane',
  T_SHOP_TT_STARTED: 'Rozpoczęte',
  T_SHOP_TT_PREPARE_FROM: 'Przygotować od',
  T_SHOP_TT_STARTING_TIME: 'Czas rozpoczęcia',
  T_SHOP_TT_ENDING_TIME: 'Czas zakończenia',
  T_SHOP_TT_STEP_NO: 'Nr kroku',
  T_SHOP_TT_PLANNING_SORTING: 'Sortowanie planu (1-99)',
  T_SHOP_TT_PF_COMPLETED: 'Proces zakończony',
  T_SHOP_TT_EMPTY_RACK: 'Pusty stojak',
  T_SHOP_TT_NO_INFO: '---',
  T_SHOP_TT_ORDER: 'Zlecenie',
  T_SHOP_TT_SERIAL_NO: 'Nr seryjny',
  T_SHOP_TT_PART_DES: 'Opis części',
  T_SHOP_TT_PART_NO: 'Nr części',
  T_SHOP_TT_CUSTOMER: 'Klient',
  T_SHOP_TT_IN_STAGE: 'w etapie:',

  // SHOPLAYOUT, PROCESS FLOWS
  T_SHOP_PF_STATUS_FREE: 'Pusty',
  T_SHOP_PF_STATUS_OCCUPIED: 'zajęte',
  T_SHOP_PF_STATUS_FINISHED: 'Zakończone',
  T_SHOP_PF_STATUS_ERROR: 'Błąd',
  T_SHOP_PF_STATUS_LOCKED: 'ZABLOKOWANE',
  T_SHOP_PF_STATUS_RESERVED: 'Zarezerwowane',
  T_SHOP_PF_STATUS_LOCKED_HIGH_LOAD: 'Zablokowane z powodu dużego obciążenia',
  T_SHOP_PF_STATUS_OILY: 'oleiste',
  T_SHOP_PF_STATUS_WAIT_FOR_PLC: 'Oczekiwanie na PLC',
  T_SHOP_PF_STATUS_EMPTY_RACK: 'Pusty stojak',
  T_SHOP_PF_STATUS_EMPTY_RACK_ANNEALING_GOODS: 'Pusty stojak dla wyrobów do wyżarzania',
  T_SHOP_PF_STATUS_BASE_TRAY_ANNEALING_GOODS: 'Podstawowy stojak dla wyrobów do wyżarzania',
  T_SHOP_PF_STATUS_EMPTY_RACK_STAPLE_GOODS: 'Pusty stojak dla ładunków pakietowych',
  T_SHOP_PF_STATUS_BASE_TRAY_PRODUCTS: 'Podstawowy stojak dla produktów',
  T_SHOP_PF_STATUS_TEST_LOAD: 'Wsad testowy',
  T_SHOP_PF_STATUS_BOTTOM_OILY: 'oleiste od spodu',
  T_SHOP_PF_STATUS_CHECK: 'Kontrola',
  T_SHOP_PF_STATUS_PROCESS_CHECK: 'Kontrola procesu',
  T_SHOP_PF_STATUS_WASCH_CHECK: 'Kontrola mycia',
  T_SHOP_PF_STATUS_WAIT: 'Oczekiwanie',
  T_SHOP_PF_STATUS_MANUAL: 'Ręczny',

  T_SHOP_PF_STATUS_RESERVED_TEXT: 'Zarezerwowane przez ',
  T_SHOP_PF_STATUS_RESERVED_TEXT_LOAD: 'Numer ładunku:',

  T_SHOP_PF_PRIORITY_NORMAL: 'Normalny',
  T_SHOP_PF_PRIORITY_HURRIED: 'Pośpieszny',
  T_SHOP_PF_PRIORITY_VERY_FAST: 'Bardzo szybki',
  T_SHOP_PF_PRIORITY_AS_FAST_AS_POSSIBLE: 'Jak najszybciej',

  // Automat shoplayout legend – Polish translation
  T_SHOP_PF_LEGEND_TEST_PASS: 'Zatwierdzono',
  T_SHOP_PF_LEGEND_CLEANED: 'Wyczyszczone',
  T_SHOP_PF_LEGEND_PRE_OXIDIZED: 'Wstępnie utlenione',
  T_SHOP_PF_LEGEND_PRE_HEATED: 'Wstępnie podgrzane',
  T_SHOP_PF_LEGEND_TEMPERED: 'Hartowane',
  T_SHOP_PF_LEGEND_UNLOAD: 'Rozładunek',
  T_SHOP_PF_LEGEND_EMPTY_RACK: 'Pusty regał',
  T_SHOP_PF_LEGEND_LOCKED: 'Zablokowany',
  T_SHOP_PF_LEGEND_DELAYED_START: 'Czas rozpoczęcia',
  T_SHOP_PF_LEGEND_PREHEATING: '->Podgrzewanie',
  T_SHOP_PF_LEGEND_TO_IQ: '->IQ',
  T_SHOP_PF_LEGEND_TO_DROPPING: '->Upuszczanie',
  T_SHOP_PF_LEGEND_TO_COOLING: '->Chłodzenie',
  T_SHOP_PF_LEGEND_TO_WASCHING: '->Mycie',
  T_SHOP_PF_LEGEND_TO_TEMPERING: '->Hartowanie',
  T_SHOP_PF_LEGEND_LOAD_IN_PROGRESS: 'Ładunek w trakcie',
  T_SHOP_PF_LEGEND_IN_PROCESS_FLOW: 'W procesie',
  T_SHOP_PF_LEGEND_TEST_LOAD: 'Test Ładunku',
  T_SHOP_PF_LEGEND_FOR_WASHING: 'Do mycia',
  T_SHOP_PF_LEGEND_FOR_NITRIDING: 'Do azotowania',
  T_SHOP_PF_LEGEND_TEST_CHARGE_IN_PROGRESS: 'Test ładunku w toku',
  T_SHOP_PF_LEGEND_INCOMPLETE: 'Niekompletny',
  T_SHOP_PF_LEGEND_TO_PACKAGING: 'Do pakowania',
  T_SHOP_PF_LEGEND_FINISHED_LOAD: 'Ładunek zakończony',
  T_SHOP_PF_LEGEND_FINISHED_WITH_ALARMS: 'Ładunek zakończony z alarmami',
  T_SHOP_PF_LEGEND_WAIT: 'Czekaj',
  T_SHOP_PF_LEGEND_TEST_WAIT: 'Czekaj',
  T_SHOP_PF_LEGEND_TEST_LOAD_URGENT: 'Pilny test ładunku',
  T_SHOP_PF_LEGEND_TEST_PROCESS_INSPECTION: 'Inspekcja procesu',
  T_SHOP_PF_LEGEND_TEST_WASH_INSPECTION: 'Inspekcja mycia',
  T_SHOP_PF_LEGEND_IN_BETWEEN: 'Między',
  T_SHOP_PF_LEGEND_RESERVED: 'Zarezerwowane',
  T_SHOP_PF_LEGEND_GR_G: 'GR-G.',
  T_SHOP_PF_LEGEND_LR_G: 'LR-G.',
  T_SHOP_PF_LEGEND_GR_S: 'GR-S.',
  T_SHOP_PF_LEGEND_LR_S: 'LR-S.',

  T_SHOP_PF_MESSAGE_SECOND_LOAD_ASSIGNED: 'Do tego wsadu przypisano drugi wsad - najpierw usuń przypisanie',
  T_SHOP_PF_MESSAGE_CONFIRMATION_NEXT_STAGE: 'Czy na pewno chcesz ręcznie zatrzymać ten etap i uruchomić następny?',
  T_SHOP_PF_MESSAGE_WARNING_DANGEROUS: '!!! UWAGA !!! To jest bardzo niebezpieczna operacja',
  T_SHOP_PF_MESSAGE_NOT_VALID: 'Nieprawidłowe',
  T_SHOP_PF_MESSAGE_STEP_MANIPULATION: 'Manipulacja krokiem',
  T_SHOP_PF_MESSAGE_ERROR_PLACE: 'Błąd: Miejsce:',
  T_SHOP_PF_MESSAGE_ERROR_PF: 'Błąd: Proces:',
  T_SHOP_PF_MESSAGE_IS_NOT_OCCUPIED: 'nie jest zajęte',
  T_SHOP_PF_MESSAGE_HAS_NO_LOAD: 'nie ma wsadu',
  T_SHOP_PF_MESSAGE_HAS_NO_STAGE: 'nie ma etapu',
  T_SHOP_PF_MESSAGE_NO_FURTHER_LOADS_PERMITTED: 'Dalsze wsady niedozwolone!',
  T_SHOP_PF_MESSAGE_SELECT_START_TIME: 'Wybierz czas startu',
  T_SHOP_PF_MESSAGE_LOAD_READY_NEW_STEP: 'Wsad gotowy. Ponownie aktywować z powodu nowego kroku?',
  T_SHOP_PF_MESSAGE_SELECT_PERSON_FOR_PACK: 'Wybierz osobę do <pakowania wsadu>',
  T_SHOP_PF_MESSAGE_MAY_NOT_BE_EMPTY: 'nie może być puste',
  T_SHOP_PF_MESSAGE_ENTER_BOX_NO: 'Wprowadź numer skrzynki!',
  T_SHOP_PF_MESSAGE_ENTER_VALID_BOX_NO: 'Wybierz prawidłowy numer skrzynki (10..49)',
  T_SHOP_PF_MESSAGE_CANNOT_PICK_TALL_LOADS: 'Nie można podnieść wysokich wsadów',
  T_SHOP_PF_MESSAGE_BOOK_PROCESS_FLOW: 'Zarejestruj proces',
  T_SHOP_PF_MESSAGE_SELECT_TRAY_CODE: 'Wybierz kod stojaka (1..99)!',
  T_SHOP_PF_MESSAGE_ENTER_LOAD_ORDER_1: 'Wybierz kolejność wsadu (1..700)',
  T_SHOP_PF_MESSAGE_ENTER_LOAD_ORDER_2: 'Wybierz kolejność wsadu mniejszą niż 701!',
  T_SHOP_PF_MESSAGE_ENTER_VALID_TOOLING: 'Wprowadź prawidłowe oprzyrządowanie',
  T_SHOP_PF_MESSAGE_WAIT_PLC_CHECK_PROGRESS: 'Proszę czekać. Trwa sprawdzanie z PLC ...',
  T_SHOP_PF_MESSAGE_MOVE_DATA_TARGET_OCCUPIED: 'Miejsce docelowe jest już zajęte. Czy na pewno chcesz je nadpisać?',
  T_SHOP_PF_MESSAGE_MOVE_DATA_TARGET_OCCUPIED_TRAY: 'PlatzControl.80=Miejsce docelowe jest już zajęte pustą tacą. Czy na pewno chcesz je nadpisać?',
  T_SHOP_PF_MESSAGE_MOVE_OCCUPIED_DELETE: 'jest zajęte → być może możesz je usunąć.',
  T_SHOP_PF_MESSAGE_MOVE_OCCUPIED_LOAD_DELETE: 'jest zajęte przez ładunek → być może możesz go usunąć.',
  T_SHOP_PF_MESSAGE_OCCUPY_ALREADY_OCCIPIED: 'Miejsce jest już zajęte!',
  T_SHOP_PF_MESSAGE_OCCUPY_CONFIRM: 'Czy chcesz oznaczyć miejsce jako zajęte?',
  T_SHOP_PF_MESSAGE_NO_PF_CONTENT_FOUND: 'Nie znaleziono zawartości przepływu procesu',
  T_SHOP_PF_MESSAGE_CONFIRMATION_DATA_ON_PLACE_DELETION: 'Dane w miejscu, ale brak bitu ZAJĘTY! Odblokowanie oznaczałoby usunięcie. <Tak> == wykonać mimo tego?',
  T_SHOP_PF_MESSAGE_CONFIRMATION_DATA_ON_PLACE_DELETION_LAST_WARNING: '!!!! OSTATNIE OSTRZEŻENIE !!! odblokowanie oznacza, że USUNIESZ dane miejsca   <Tak> == Usuń?',
  T_SHOP_PF_MESSAGE_NO_RIGHTS_FOR_ACTION: 'Nie masz uprawnień do wykonania tej czynności. Skontaktuj się z administratorem.',

  // Process flow table columns
  T_SHOP_PF_COLUMN_OPERATION_LABEL: 'Operacja',
  T_SHOP_PF_COLUMN_WORKPLACE_LABEL: 'Stanowisko',
  T_SHOP_PF_COLUMN_RECIPE_LABEL: 'Receptura',
  T_SHOP_PF_COLUMN_DESCRIPTION_LABEL: 'Opis',
  T_SHOP_PF_COLUMN_TIME_LABEL: 'Czas',
  T_SHOP_PF_COLUMN_TIME_TOOLTIP: 'Czas oczekiwania na tym stanowisku (min)',
  T_SHOP_PF_COLUMN_ACK_LABEL: 'R',
  T_SHOP_PF_COLUMN_ACK_TOOLTIP: 'Potwierdź',
  T_SHOP_PF_COLUMN_AUTOSKIP_LABEL: 'A',
  T_SHOP_PF_COLUMN_AUTOSKIP_TOOLTIP: 'Automatyczne pomijanie',
  T_SHOP_PF_COLUMN_MANUAL_LABEL: 'H',
  T_SHOP_PF_COLUMN_MANUAL_TOOLTIP: 'Ręczny start/stop kroku',
  T_SHOP_PF_COLUMN_QM_RELEVAT_LABEL: 'Q',
  T_SHOP_PF_COLUMN_QM_RELEVAT_TOOLTIP: 'Krok istotny dla jakości (QM)',
  T_SHOP_PF_COLUMN_QM_RELEVAT_WITTEN_LABEL: 'F',
  T_SHOP_PF_COLUMN_QM_RELEVAT_WITTEN_TOOLTIP: 'Obróbka zewnętrzna',
  T_SHOP_PF_COLUMN_PROD_STAT_RELEVANT_LABEL: 'P',
  T_SHOP_PF_COLUMN_PROD_STAT_RELEVANT_TOOLTIP: 'Krok istotny dla statystyk produkcji',
  T_SHOP_PF_COLUMN_MOVE_STEP_LABEL: 'C',
  T_SHOP_PF_COLUMN_MOVE_STEP_TOOLTIP: 'Przesuń krok bliżej następnego (automatyczne planowanie)',
  T_SHOP_PF_COLUMN_PTIME_LABEL: 'PCzas',
  T_SHOP_PF_COLUMN_PTIME_TOOLTIP: 'Czas planowania',
  T_SHOP_PF_COLUMN_MIN_HOLD_LABEL: 'Min. czas',
  T_SHOP_PF_COLUMN_MIN_HOLD_TOOLTIP: 'Minimalny czas przetrzymania w recepturze (min)',
  T_SHOP_PF_COLUMN_MAX_HOLD_LABEL: 'Max. czas',
  T_SHOP_PF_COLUMN_MAX_HOLD_TOOLTIP: 'Maksymalny czas przetrzymania w recepturze (min)',
  T_SHOP_PF_COLUMN_WISH_TIME_LABEL: 'Czas planu',
  T_SHOP_PF_COLUMN_WISH_TIME_TOOLTIP: 'Docelowy czas planowania (min)',
  T_SHOP_PF_COLUMN_STEPNO_FROM_LABEL: 'Połączone od kroku',
  T_SHOP_PF_COLUMN_STEPNO_FROM_TOOLTIP: 'Nr kroku (od którego obowiązuje min/max czas)',
  T_SHOP_PF_COLUMN_STEPNO_TO_LABEL: 'Połączone do kroku',
  T_SHOP_PF_COLUMN_STEPNO_TO_TOOLTIP: 'Nr kroku (do którego obowiązuje min/max czas)',
  T_SHOP_PF_COLUMN_TRANSFER_LABEL: 'Czas transferu SP (sek)',
  T_SHOP_PF_COLUMN_TRANSFER_TOOLTIP: 'Czas transferu SP w sekundach z poprzedniego kroku',
  T_SHOP_PF_COLUMN_PLAN_TRANSFER_LABEL: 'Czas transferu planu (min)',
  T_SHOP_PF_COLUMN_PLAN_TRANSFER_TOOLTIP: 'Planowany czas transferu w minutach',
  T_SHOP_PF_COLUMN_DROP_TIME_LABEL: 'Czas zrzutu SP (sek)',
  T_SHOP_PF_COLUMN_DROP_TIME_TOOLTIP: 'Czas zrzutu SP w sekundach',
  // runtime
  T_SHOP_PF_COLUMN_START_LABEL: 'Start',
  T_SHOP_PF_COLUMN_END_LABEL: 'Koniec',
  T_SHOP_PF_COLUMN_PLANNED_WP_LABEL: 'Zaplanowane dla stanowiska',
  T_SHOP_PF_COLUMN_STATUS_LABEL: 'Status',
  T_SHOP_PF_COLUMN_CHECKED_LABEL: 'Sprawdzone',
  T_SHOP_PF_COLUMN_TRANSFER_TIME_LABEL: 'Czas transferu (sek)',
  T_SHOP_PF_COLUMN_SOAK_TIME_LABEL: 'Czas namaczania (pomiar)',
  T_SHOP_PF_COLUMN_SOAK_TIME_TOOLTIP: 'Czas namaczania partii dla tego kroku',
  T_SHOP_PF_COLUMN_TEMP_AFTER_LABEL: 'Temp. po planowaniu',
  T_SHOP_PF_COLUMN_TEMP_AFTER_TOOLTIP: 'Temperatura instalacji po zaplanowaniu kroku',
  T_SHOP_PF_COLUMN_PREPARE_START_LABEL: 'Start przygotowania',
  T_SHOP_PF_COLUMN_PREPARE_START_TOOLTIP: '(Planowany) czas rozpoczęcia przygotowania',
  T_SHOP_PF_COLUMN_DYN_SETPOINT_LABEL: 'Dynamiczny punkt zadany',
  T_SHOP_PF_COLUMN_DYN_SETPOINT_TOOLTIP: 'Dynamiczny punkt zadany z kroku procesu',

  // Process flow table columns - parts
  T_SHOP_PF_COLUMN_PART_POS_LABEL: 'Pozycja',
  T_SHOP_PF_COLUMN_PART_POS_TOOLTIP: 'Numer pozycji (#)',
  T_SHOP_PF_COLUMN_PART_PARTNO_TOOLTIP: 'Wprowadź numer części (lub kliknij na liście)',
  T_SHOP_PF_COLUMN_PART_DESC_LABEL: 'Opis części:',
  T_SHOP_PF_COLUMN_PART_DESC_TOOLTIP: 'Opis części',
  T_SHOP_PF_COLUMN_PART_AMOUNT_LABEL: 'Ilość',
  T_SHOP_PF_COLUMN_PART_AMOUNT_2_LABEL: 'Po kontroli objętości',
  T_SHOP_PF_COLUMN_PART_AMOUNT_TOTAL_WEIGHT_LABEL: 'Łączna waga',
  T_SHOP_PF_COLUMN_PART_ORDER_LABEL: 'Zamówienie',
  T_SHOP_PF_COLUMN_PART_ORDER_TOOLTIP: 'Wprowadź numer zamówienia (lub kliknij na liście)',
  T_SHOP_PF_COLUMN_PART_ORDER_SELECTION_LABEL: 'Przekazanie',
  T_SHOP_PF_COLUMN_PART_ORDER_REMARK_LABEL: 'Nr seryjny',
  T_SHOP_PF_COLUMN_PART_ORDER_REMARK_LABEL_2: 'Uwaga do zamówienia',
  T_SHOP_PF_COLUMN_PART_CUSTOM_FIELD_3: 'Pole‑3',
  T_SHOP_PF_COLUMN_PART_CUSTOM_FIELD_4: 'Pole‑4',
  T_SHOP_PF_COLUMN_PART_EDIT_IN_WINDOW_LABEL: 'dodatkowe',
  T_SHOP_PF_COLUMN_PART_EDIT_IN_WINDOW_TOOLTIP: 'Edytuj w oknie',
  T_SHOP_PF_COLUMN_PART_TYPE_LABEL: 'Typ',
  T_SHOP_PF_COLUMN_PART_TYPE_TOOLTIP: 'Typ (Własny, Obcy, Przychody)',
  T_SHOP_PF_COLUMN_PART_START_BASEDATA_PARTS_LABEL: '-> Otwórz okno części',
  T_SHOP_PF_COLUMN_PART_START_BASEDATA_PARTS_TOOLTIP: 'Uruchom bazowe dane części',
  T_SHOP_PF_COLUMN_PART_INTERNAL_ORDER_NO_LABEL: 'Wewnętrzne',
  T_SHOP_PF_COLUMN_PART_INTERNAL_ORDER_NO_TOOLTIP: 'ID zamówienia (wewnętrzne)',
  T_SHOP_PF_COLUMN_PART_PROBE_LABEL: 'Próbka',
  T_SHOP_PF_COLUMN_PART_MATERIAL_LABEL: 'Materiał',
  T_SHOP_PF_COLUMN_PART_DRAWING_NO_LABEL: 'Rysunek nr',

  // edit pf
  T_SHOP_PF_DIALOG_EDIT_CAPTION: 'Edycja procesu',

  T_SHOP_PF_MESSAGE_PLACE_ALREADY_RESERVED: 'Nowe miejsce jest już zarezerwowane - zmiana niedozwolona',
  T_SHOP_PF_MESSAGE_NUMBER_USED_1: 'Numer',
  T_SHOP_PF_MESSAGE_NUMBER_USED_2: 'jest już używany!',
  T_SHOP_PF_MESSAGE_NUMBER_USED_3: 'Numer odrzucony!',

  T_SHOP_PF_MESSAGE_NOT_COMPLETE_1: 'w etapie',
  T_SHOP_PF_MESSAGE_NOT_COMPLETE_2: 'niekompletny',

  T_SHOP_PF_MESSAGE_AMMOUNT_TOO_SMALL_1: 'Ilość zbyt mała → użyj procesu:',
  T_SHOP_PF_MESSAGE_AMMOUNT_TOO_SMALL_2: 'zamiast procesu:',

  T_SHOP_PF_MESSAGE_NO_PART_FOR_PF_1: 'Błąd wsadu: nie znaleziono procesu dla części',
  T_SHOP_PF_MESSAGE_NO_PART_FOR_PF_2: 'oraz',

  // delete place
  T_SHOP_PF_DIALOG_DELETE_CAPTION: 'Usuń miejsce',
  T_SHOP_PF_DIALOG_DELETE_CONFIRM_CAPTION: 'Potwierdzenie usunięcia',
  T_SHOP_PF_DIALOG_DELETE_CONFIRM_TEXT: 'Czy na pewno chcesz usunąć wszystkie dane miejsca nr (PFlow + zawartość)?',
  T_SHOP_PF_DIALOG_DELETE_PERSON_CAPTION: 'Wybierz osobę, która rozładowała wsad',
  T_SHOP_PF_DIALOG_DELETE_PERSON_ERROR: 'Bez wskazania osoby rozładowującej nie można usunąć miejsca',
  T_SHOP_PF_DIALOG_DELETE_RUNNING_PERMISSION_ERROR: 'Brak uprawnień do usunięcia aktywnego wsadu',
  T_SHOP_PF_DIALOG_DELETE_PLACE_NOT_FOUND_ERROR: 'Błąd: miejsce w DB (b_wp_aut) nie znalezione:',

  // move place
  T_SHOP_PF_DIALOG_MOVE_TEXT: 'Przenieś dane z tego miejsca do miejsca nr',

  // manual command
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_CAPTION: 'Przemieść na miejsce przy użyciu polecenia ręcznego',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_TEXT: 'Wprowadź celowe miejsce (polecenie ręczne):',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_NOT_VALID: 'Błąd: miejsce {0} nie jest dopuszczalne',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_COMMAND_FOR_UNLOAD: 'Brak polecenia ręcznego do miejsc rozładunku',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_COMMAND_FOR_LOADING: 'Nie można przenieść ładunku do stacji rozładunku',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_NOT_FOUND_B_WP_AUT: 'Błąd: miejsce {0} nie znaleziono w bazie danych b_wp_aut',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_CANNOT_BE_GIVEN: 'Użycie urządzenia jako miejsca docelowego dla polecenia ręcznego jest niedozwolone. Proszę użyć procesu.',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_IS_NOT_OCCUPIED: 'Błąd: miejsce {0} nie jest zajęte',

  // Cancel manual command
  T_SHOP_PF_DIALOG_CANCEL_MANUAL_COMMAND: 'Czy na pewno chcesz anulować ręczne polecenie samochodu ?',

  // Reactivate pf
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_TEXT: 'Ponownie aktywuj usunięty ProcessFlowNo: (bez - : YYNNNNN (YY=Rok NNNNN=Numer)',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_NOT_FOUND: 'Nie znaleziono ProcessFlowNo',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_ALREADY_ON_PLACE: 'To ProcessFlowNo znajduje się już na miejscu',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_INPUT: 'ProcessFlowNo musi zawierać dokładnie 7 znaków (2=Rok 5=Numer)',

  T_SHOP_PF_DIALOG_CAPTION_INFORMATION: 'Informacja',
  T_SHOP_PF_DIALOG_CAPTION_MOVE: 'Przenieś',
  T_SHOP_PF_DIALOG_CAPTION_CONFIRMATION_OVERWRITE: 'Potwierdzenie przed nadpisaniem',
  T_SHOP_PF_DIALOG_CAPTION_CONFIRM: 'Proszę potwierdzić',

  T_SHOP_PF_BTN_LOCK_CAPTION: 'Zablokuj',
  T_SHOP_PF_BTN_LOCK_TOOLTIP: 'Zablokuj miejsce',
  T_SHOP_PF_BTN_LOCK_UNLOCK_CAPTION: 'Odblokuj',
  T_SHOP_PF_BTN_LOCK_UNLOCK_TOOLTIP: 'Odblokuj miejsce',

  T_SHOP_PF_BTN_UNLOAD_CAPTION: 'Rozładuj',
  T_SHOP_PF_BTN_UNLOAD_TOOLTIP: 'Rozładuj',

  T_SHOP_PF_BTN_LOAD_PARTS_CAPTION: 'Załaduj części',
  T_SHOP_PF_BTN_LOAD_PARTS_TOOLTIP: 'Załaduj części',

  T_SHOP_PF_BTN_MODIFY_PROCESS_FLOW_CAPTION: 'Zmień proces',
  T_SHOP_PF_BTN_MODIFY_PROCESS_FLOW_TOOLTIP: 'Zmień proces',

  T_SHOP_PF_BTN_START_PROCESS_FLOW_CAPTION: 'Zezwól na start',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_TOOLTIP: 'Zezwól na start',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_MODIFY_TIME_CAPTION: 'Zmień czas startu',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_MODIFY_TIME_TOOLTIP: 'Zmień czas startu',

  T_SHOP_PF_BTN_ENTER_PROCESS_FLOW_CAPTION: 'Wprowadź proces',
  T_SHOP_PF_BTN_ENTER_PROCESS_FLOW_TOOLTIP: 'Wprowadź proces',

  T_SHOP_PF_BTN_MANUAL_CAR_COMMAND_CAPTION: 'Ręczne sterowanie wózkiem',
  T_SHOP_PF_BTN_MANUAL_CAR_COMMAND_TOOLTIP: 'Ręczne sterowanie wózkiem',

  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_CAPTION: 'Anuluj polecenie ręczne',
  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_TOOLTIP: 'Anuluj polecenie ręczne',
  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_CAPTION_TO: 'Anuluj polecenie ręczne do',

  T_SHOP_PF_BTN_DELETE_PLACE_CAPTION: 'Usuń miejsce',
  T_SHOP_PF_BTN_DELETE_PLACE_TOOLTIP: 'Usuń miejsce',
  T_SHOP_PF_BTN_DELETE_PLACE_UNLOAD_CAPTION: 'Rozładuj',
  T_SHOP_PF_BTN_DELETE_PLACE_UNLOAD_TOOLTIP: 'Rozładuj',

  T_SHOP_PF_BTN_OCCUPY_CAPTION: 'Zajmij',
  T_SHOP_PF_BTN_OCCUPY_TOOLTIP: 'Zajmij',

  T_SHOP_PF_BTN_SHOW_LOADS_CAPTION: 'Pokaż wsady',
  T_SHOP_PF_BTN_SHOW_LOADS_TOOLTIP: 'Pokaż wsady',
  T_SHOP_PF_BTN_REACTIVATE_PROCESS_FLOW_CAPTION: 'Reaktywuj proces',
  T_SHOP_PF_BTN_REACTIVATE_PROCESS_FLOW_TOOLTIP: 'Reaktywuj usunięty proces na pustym stojaku',

  T_SHOP_PF_BTN_STEP_MANIPULATION_CAPTION: 'Manipulacja krokiem',
  T_SHOP_PF_BTN_STEP_MANIPULATION_TOOLTIP: 'Manipulacja krokiem',

  T_SHOP_PF_BTN_MOVE_TO_PLACE_CAPTION: 'Przenieś',
  T_SHOP_PF_BTN_MOVE_TO_PLACE_TOOLTIP: 'Przenieś',

  T_SHOP_PF_BTN_SAVE_IMAGE: 'Zapisz obraz',
  T_SHOP_PF_BTN_SAVE_IMAGE_EXISTS: 'Obraz dostępny',

  T_SHOP_PF_BTN_LOAD_TESTED_CAPTION: 'Zakończ/edytuj test wsadu',
  T_SHOP_PF_BTN_LOAD_TESTED_TOOLTIP: 'Ładunek sprawdzony',
  T_SHOP_PF_BTN_LOAD_TESTED_TEST_LOAD_CAPTION: 'Rozpocznij test wsadu',
  T_SHOP_PF_BTN_LOAD_TESTED_TEST_LOAD_TOOLTIP: 'Sprawdź Ładunek',
  T_SHOP_PF_BTN_LOAD_TESTED_CHECK_OK_CAPTION: 'Sprawdzono OK',
  T_SHOP_PF_BTN_LOAD_TESTED_CHECK_OK_TOOLTIP: 'Kontrola pośrednia została przeprowadzona',
  T_SHOP_PF_BTN_LOAD_TESTED_PROCESS_CHECK_OK_CAPTION: 'Kontrola procesu OK',
  T_SHOP_PF_BTN_LOAD_TESTED_PROCESS_CHECK_OK_TOOLTIP: 'Kontrola procesu została przeprowadzona',
  T_SHOP_PF_BTN_LOAD_TESTED_WASH_CHECK_OK_CAPTION: 'Kontrola mycia OK',
  T_SHOP_PF_BTN_LOAD_TESTED_WASH_CHECK_OK_TOOLTIP: 'Kontrola mycia została przeprowadzona',
  T_SHOP_PF_BTN_LOAD_TESTED_END_WAIT_CAPTION: 'Zakończ oczekiwanie',
  T_SHOP_PF_BTN_LOAD_TESTED_END_WAIT_TOOLTIP: 'Oczekiwanie zakończone',

  T_SHOP_PF_BTN_CHARGE_IN_QS_WORK_CAPTION: 'Uruchomienie/Zatrzymanie przetwarzania QA',
  T_SHOP_PF_BTN_CHARGE_IN_QS_WORK_TOOLTIP: 'Uruchomienie/Zatrzymanie przetwarzania QA',

  T_SHOP_PF_CHECKBOX_TEST_LOAD_CAPTION: 'Test ładunek',
  T_SHOP_PF_CHECKBOX_TEST_LOAD_TOOLTIP: 'Test ładunek',
  T_SHOP_PF_CHECKBOX_END_TEST_LOAD_CAPTION: 'Zakończ pilny test',
  T_SHOP_PF_CHECKBOX_END_TEST_LOAD_TOOLTIP: 'Zakończ pilny test',
  T_SHOP_PF_CHECKBOX_NIO_TEST_LOAD_CAPTION: 'Test NIO',
  T_SHOP_PF_CHECKBOX_NIO_TEST_LOAD_TOOLTIP: 'Test NIO',
  T_SHOP_PF_CHECKBOX_IN_QA_WORK_CAPTION: '..W przetwarzaniu QA..',
  T_SHOP_PF_CHECKBOX_IN_QA_WORK_TOOLTIP: '..W przetwarzaniu QA..',

  T_SHOP_PF_UNLOAD_SELECT_PLACE_CAPTION: 'Wybierz miejsce rozładunku:',
  T_SHOP_PF_UNLOAD_ERROR_MAGAZINE_ONLY: 'Rozładunek jest możliwy tylko na miejscach magazynowych',
  T_SHOP_PF_UNLOAD_ERROR_INVALID_OPTION: 'Podano nieprawidłowe miejsce do rozładunku!',
  T_SHOP_PF_UNLOAD_ERROR_ALREADY_TARGET: ' (Miejsce już jest celem miejsca: {0})',
  T_SHOP_PF_UNLOAD_ERROR_CAR_APPROACHED: ' (Miejsce jest obecnie podjeżdżane przez pojazd)',

  T_SHOP_PF_TEST_DIALOG_TITLE: 'Ładunek przetestowany',
  T_SHOP_PF_TEST_DIALOG_TEXT: 'Wprowadź nazwę i czas',
  T_SHOP_PF_TEST_DIALOG_INPUT_NAME_DATE: 'Wprowadź nazwę i czas',
  T_SHOP_PF_TEST_DIALOG_INPUT_NAME_DATE_CAPTION: 'Ładunek przetestowany',
  T_SHOP_PF_TEST_DIALOG_INPUT_TEST_STARTED_TEXT: 'Rozpoczęto test ładowania  {0} ',
  T_SHOP_PF_TEST_DIALOG_ERROR_ACTION_NOT_POSSIBLE: 'Operacja niemożliwa',
  T_SHOP_PF_TEST_DIALOG_ERROR_PLACE_NOT_FOUND: 'Błąd: miejsce w bazie danych (b_wp_aut) nie znalezione: {0}',

  T_SHOP_PF_TEST_DIALOG_CONFIRM_NOT_READY_CHECKED: 'Ładunek jeszcze nie gotowy! Czy naprawdę oznaczyć jako przetestowany?',
  T_SHOP_PF_TEST_ENTER_RESULT_DIALOG_TEXT: 'Czy chcesz wprowadzić wynik testu?',
  T_SHOP_PF_TEST_DIALOG_RESULT_SELECTION_PASSED: 'Test zaliczony',
  T_SHOP_PF_TEST_DIALOG_RESULT_SELECTION_NOT_PASSED: 'Test niezaliczony',

  // LOAD INFO
  T_LOAD_STATUS_WAIT_FOR: 'Oczekiwanie na',
  T_LOAD_STATUS_PLANNED_START: 'planowany start',
  T_LOAD_STATUS_EXTERNAL_START: 'start zewnętrzny',
  T_LOAD_STATUS_NOT_READY: 'NIEGOTOWE!',
  T_LOAD_STATUS_CONT_MANUAL: 'Sterownik w trybie ręcznym',
  T_LOAD_STATUS_STEP_1: 'z',
  T_LOAD_STATUS_STEP_2: 'Krok nr',
  T_LOAD_STATUS_RECIPE_FINISHED: 'Receptura zakończona',
  T_LOAD_STATUS_OCCUPIED: 'zajęte',
  T_LOAD_STATUS_WILL_START: 'wkrótce rozpocznie',
  T_LOAD_STATUS_WAIT_FOR_RECIPE: 'Oczekiwanie na recepturę',
  T_LOAD_STATUS_WAIT_FOR_LOAD: 'Oczekiwanie na wsad',
  T_LOAD_STATUS_GET_OUT: 'wyjazd',
  T_LOAD_STATUS_RECIPE: 'Receptura',
  T_LOAD_STATUS_WANT: 'chce',
  T_LOAD_STATUS_PF_COMPLETED: 'Przebieg procesu zakończony',
  T_LOAD_STATUS_LOCKED: 'zablokowane',
  T_LOAD_STATUS_PRIORITY: 'Priorytet',
  T_LOAD_STATUS_NOT_OCCUPIED: '! niezajęte !',
  T_LOAD_STATUS_NO_TAG: 'Brak tagu',
  T_LOAD_STATUS_PREPARE: 'PRZYGOTOWANIE',
  T_LOAD_STATUS_RESERVED_BY: 'Zarezerwowane przez',

  // OGÓLNE BŁĘDY
  T_GENERAL_ERROR_NOT_IMPLEMENTED: 'Nie zaimplementowano',
  T_GENERAL_ERROR_UNSUPPORTED_ON_WORKPLACE: 'Nieobsługiwane na stanowisku pracy',
  T_GENERAL_ERROR_CAPTION: 'Błąd',
  T_GENERAL_WARNING_CAPTION: 'Ostrzeżenie',

  // Global name of part
  T_GENERAL_PART_NAME_1: 'Część n°',
  T_GENERAL_PART_NAME_2: 'Kaefig-Nr.',
  T_GENERAL_PART_NAME_3: 'Bosch-Ch.-Nr.',
  T_GENERAL_PART_NAME_4: 'Ident-Nr',

  // BŁĘDY OKNA DIALOGOWEGO WSADU
  T_LOAD_DIALOG_ERROR_NO_PARTS: 'Nie podano żadnych części!',
  T_LOAD_DIALOG_ERROR_ORDER: 'Zamówienie (poz.) \'{0}\' zostało znalezione!',
  T_LOAD_DIALOG_ERROR_PLACE_STATE: 'Miejsce musi być zajęte, ale bez danych (== puste miejsce).',
  T_LOAD_DIALOG_ERROR_PLACE_ALREADY_OCCUPIED: 'Miejsce jest już zajęte danymi.',
  T_LOAD_DIALOG_ERROR_NOT_OCCUPIED_OR_EMPTY: 'Miejsce nie jest zajęte lub tacka jest pusta!',
  T_LOAD_DIALOG_ERROR_PLACE_LOCKED: 'To miejsce jest zablokowane!',
  T_LOAD_DIALOG_ERROR_PF_FROM_ORDER_NOT_FOUND: 'Nie znaleziono przepływu procesu z zamówienia, pfTemplateId={0}.',
  T_LOAD_DIALOG_ERROR_NO_BOX_NO_FREE: 'Brak wolnego numeru Box --> Naciśnij Command-Repair...',
  T_LOAD_DIALOG_ERROR_CREATING_LOAD: 'Błąd podczas tworzenia nowego Cell-Load',
  T_LOAD_DIALOG_ERROR_NO_LOAD_FOUND: 'Nie znaleziono załadunku na stanowisku {0}',
  T_LOAD_DIALOG_ERROR_NO_PART_INFO_FOUND: 'Nie znaleziono informacji o załadowanych częściach dla stanowiska!',
  T_LOAD_DIALOG_ERROR_LOAD_ALREADY_ENABLED: 'Załadunek został już włączony do automatycznego ładowania!',
  T_LOAD_DIALOG_ERROR_NO_A_LD_TMT_HEAD: 'Błąd: nie znaleziono a_ld_tmt_head dla loadId={0}, miejsce z {1}',
  T_LOAD_DIALOG_ERROR_NO_LOAD: 'Błąd: nie znaleziono załadunku w miejscu {0}, a_ld.id={1}',

  T_GANNT_PLANNING_NO_PLANNING_INFO: 'Brak dostępnych informacji o planowaniu',
  T_GANNT_PLANNING_NO_CAR_COMMAND: 'Brak dostępnego polecenia dla wózka',

  // BELT FURNACES
  // Loading dialog
  T_BELT_LOAD_START_ERROR_LOAD_NOT_FOUND: 'Nie udało się określić danych ładunku.',
  T_BELT_LOAD_START_ERROR_LOAD_NOT_FOR_BELT: 'Dozwolone tylko dla ładowań taśmowych!',
  T_BELT_LOAD_START_ERROR_ORDER_ALREADY_ACTIVE: 'Nie można, zlecenie jest już aktywne.',
  T_BELT_LOAD_START_ERROR_ONLY_ONE_ACTIVE_ORDER: 'Może istnieć tylko jedno AKTYWNE zlecenie.',
  T_BELT_LOAD_START_ERROR_LOAD_ALREADY_STARTED: 'Nie można, partia już została uruchomiona.',
  T_BELT_LOAD_START_ERROR_LOAD_WAITING_FOR_CONDITION: 'Nie można, ponieważ istnieje już uruchomiona partia po tej partii.',
  T_BELT_LOAD_START_ERROR_ACTION_NOT_ALLOWED_FOR_ORDERS: 'Działanie niedozwolone dla zleceń.',
  T_BELT_LOAD_START_NEW_STATUS_WAS_FORCED: 'Nowy stan został wymuszony.',
  T_BELT_LOAD_START_EXECUTION_STOPPED: 'Wykonanie przerwane.',
  T_BELT_LOAD_START_ACTIVATE_ORDER_CONFIRMATION: 'Czy chcesz aktywować to zlecenie?',
  T_BELT_LOAD_START_LOAD_CONFIRMATION: 'Czy na pewno chcesz uruchomić partię?',
  T_BELT_LOAD_START_LOAD_CONDITION_NOT_REACHED_CONFIRMATION: 'Warunki nie zostały jeszcze spełnione. Czy na pewno chcesz wymusić status partii?',
  T_BELT_LOAD_START_LOADING_CONFIRMATION: 'Czy na pewno chcesz rozpocząć załadunek?',
  T_BELT_LOAD_START_LOADING_ERROR_NOT_FOR_THIS_CHARGE: 'Nie można z tą partią.',
  T_BELT_LOAD_FINISH_LOADING_DELETE_ORDER_CONFIRMATION: 'Czy naprawdę chcesz usunąć zlecenie (z wszystkimi nieuruchomionymi partiami)?',
  T_BELT_LOAD_FINISH_LOADING_STOP_CONFIRMATION: 'Czy na pewno chcesz zatrzymać załadunek?',

  // BATCH FURNACES
  T_BATCH_ERROR_CHAMBER_NOT_REMOTE_OR_ACTIVE: 'Komora nie jest zdalna lub nie gotowa ...',

  // INNE
  T_END: 'Koniec',
} satisfies Record<Tpt9800TrlTokens, string>;

const batchRecipeItemVersion: Partial<TComponentPT9800Keys<'batchRecipeItemVersion'>> = {
  correctionProfile: 'Profile korekcji',
  positions: 'Kroki',
};

const digitalInput: Partial<TComponentPT9800Keys<'digitalInput'>> = {
  useForEndControl: 'Użyj jako warunku końcowego',
};

export default {
  batchRecipeItemVersion,
  beltRecipeItemHeader,
  digitalInput,
  furnaceAlarm,
  load,
  loadState,
  material,
  planLoadInput,
  plannedPartInput,
  processFlow,
  processFlowContent,
  processFlowListItem,
  processFlowManualCommand,
  pt9800Translations,
  pusherRecipeItemHeader,
  pusherRecipeRuntimeInfo,
  recipeRuntimeInfo,
  recipeItemHeader,
  recipeItemPosition,
  recipeItemPositionHeader,
  recipeListItem,
  setPointItem,
  WorkPlaceStatus,
  WorkPlaceStatusStatus,
};
